import { Component, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { BaseItemComponent } from '../../common-items/BaseItem/base-item-form.component';
import { BaseItemProperties, DEFAULT_BASE_PROPERTIES } from '../../common-items/BaseItem/base-item-properties';

import { getPropsFrom } from '../../../../utils/xmlParser';
import { MatIcon } from '@angular/material/icon';

interface SectionItemProperties {
  text: string;
  style: string;
  allowCollapse: boolean;
  startOpen: boolean;
}

@Component({
  selector: 'lfe-section-item-form',
  imports: [CommonModule, MatIcon],
  templateUrl: './section-item-form.component.html',
  standalone: true,
  styleUrl: './section-item-form.component.css'
})
export class SectionItemFormComponent extends BaseItemComponent<SectionItemProperties> implements OnInit {
  properties: BaseItemProperties & SectionItemProperties = {
    ...DEFAULT_BASE_PROPERTIES,
    text: '',
    style: '',
    allowCollapse: false,
    startOpen: true
  };

  currentId = computed(() => this.properties.customId || this.properties.id);

  htmlId = computed(() => `section_${this.currentId()}`);

  // Parse ordinals and level from text (e.g., "1.2.3. Section Title")
  ordinals = computed(() => {
    const [firstWord] = this.properties.text.split(' ');
    if (firstWord.endsWith('.')) {
      return firstWord.split('.').filter(Boolean).map(Number);
    }
    return [];
  });

  level = computed(() => Math.max(0, this.ordinals().length - 1));

  // Get open state from FormControl
  sectionFormControl = computed(() => this.form().get(this.currentId()) as FormControl<boolean> | null);

  isOpen = computed(() => {
    const control = this.sectionFormControl();
    return control?.value ?? this.properties.startOpen;
  });

  override ngOnInit() {
    super.ngOnInit();

    // Add FormControl for this section if it doesn't exist
    const sectionId = this.currentId();
    if (!this.form().get(sectionId)) {
      this.form().addControl(sectionId, new FormControl(this.properties.startOpen));
    }
  }

  onToggleClick() {
    if (this.properties.allowCollapse) {
      const control = this.sectionFormControl();
      if (control) {
        control.setValue(!control.value);
      }
    }
  }

  extractLocalProperties(xml: Element): BaseItemProperties & SectionItemProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const allowCollapse = false;
    const startOpen = true;
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_BASE_PROPERTIES,
      id,
      customId,
      activationRule,
      isGenericRepeatItem,
      text,
      style,
      allowCollapse,
      startOpen
    };
  }
}
